/**
 * Owned in Lieu (inventory + disposal tracking).
 *
 * Shows every vehicle signed over to the tenant's own salvage entity via the
 * sign-over flow. Two states per vehicle:
 *  - In Inventory (`nts_disposed_at` is null) — still sitting in the yard
 *  - Disposed (`nts_disposed_at` set) — sold / scrapped / donated / kept
 *
 * Page features:
 *  - Filter chips: In Inventory · Disposed · All
 *  - Reconciliation column: original debt vs sale price → profit/loss colour
 *  - "Mark Disposed" button per row (opens DisposeNtsDialog)
 *  - Admin-only "Undo Disposal" to fix a wrong record
 *  - Polls every 30s + manual refresh + tab-focus refresh
 *
 * RBAC: visible to admin/dispatcher/viewer (billing role). Truck/driver cannot see.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Truck, Search, X, RotateCw, FileText, Printer } from "lucide-react";
import { Button } from "../components/ui/button";
import { listNtsImpounds, undoDisposeNtsVehicle } from "../lib/api";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import ImpoundDetailSheet from "../components/ImpoundDetailSheet";
import DisposeNtsDialog from "../components/DisposeNtsDialog";
import NtsPaperworkDialog from "../components/NtsPaperworkDialog";
import NtsMonthlyPlDialog from "../components/NtsMonthlyPlDialog";
import NtsReconciliation from "../components/nts/NtsReconciliation";
import NtsRow from "../components/nts/NtsRow";
import UndoSignoverDialog from "../components/UndoSignoverDialog";
import { salvageFull, salvageShort, tenantLocale, tenantTz } from "../lib/company";
import { hasCap, can } from "../lib/roles";

const POLL_MS = 30000;

export default function OwnedInLieuPage() {
  const { user } = useAuth();
  const isAdmin = hasCap(user, "admin");

  const [filter, setFilter] = useState("false");  // "false" = In Inventory, "true" = Disposed, "all"
  const [query, setQuery] = useState(() => {
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      return p.get("q") || "";
    }
    return "";
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailJob, setDetailJob] = useState(null);
  const [disposeJob, setDisposeJob] = useState(null);
  const [paperworkJob, setPaperworkJob] = useState(null);
  const [showPlDialog, setShowPlDialog] = useState(false);
  const [undoSignoverJob, setUndoSignoverJob] = useState(null);
  // Admin / dispatcher / billing user (viewer) can pull the month-end P&L report —
  // drivers blocked. Same RBAC as the backend endpoint enforces.
  const canViewPl = hasCap(user, "admin", "dispatcher");

  // Stale-response guard (Aug 2026 search sweep).
  const reqIdRef = useRef(0);

  const refresh = useCallback(async (showSpinner = false) => {
    const myReqId = ++reqIdRef.current;
    // Silent by default: the 30s poll and filter switches update the rows in
    // place rather than blanking the table four times a minute. Only the
    // first load shows the spinner.
    if (showSpinner) setLoading(true);
    try {
      const data = await listNtsImpounds(filter);
      if (myReqId !== reqIdRef.current) return;
      setRows(data);
    } catch {
      if (myReqId !== reqIdRef.current) return;
      toast.error(`Failed to load ${salvageShort()} inventory`);
    } finally {
      // Clear on the LATEST request regardless of who set it: a silent
      // refetch overtaking a spinner request would otherwise leave the
      // spinner up forever (seen on Storage Yards, Oct 2026).
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [filter]);

  useEffect(() => { refresh(rows.length === 0); }, [refresh]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-poll every 30s while tab is visible; pauses when backgrounded.
  useEffect(() => {
    const tick = () => { if (!document.hidden) refresh(); };
    const id = setInterval(tick, POLL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  // Refresh when user tabs back into the window — picks up dispatcher edits
  // made on another device.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  const manualRefresh = async () => {
    setRefreshing(true);
    try { await refresh(); } finally { setTimeout(() => setRefreshing(false), 300); }
  };

  // Feb 2026 — printable "Disposal Worklist" for the owned-in-lieu inventory.
  // Prints ONLY the currently-in-inventory rows (never-disposed) regardless
  // of the active filter chip — the worklist is used by the yard worker
  // to physically dispose of vehicles as time permits, so showing already-
  // disposed rows would be noise. Layout mirrors the Yard Stock Take
  // sheet (A4, tick column, manual sign-off footer) for muscle-memory.
  const [printingWorklist, setPrintingWorklist] = useState(false);
  const onPrintDisposalWorklist = useCallback(() => {
    setPrintingWorklist(true);
    try {
      const inInventory = (rows || []).filter(
        (r) => !r.nts_disposed_at && !r.nts_archived_at,
      );
      // Sort alphabetically by rego — same convention as the yard sheet
      // so the worker can walk down the printed list in order.
      const sorted = [...inInventory].sort((a, b) =>
        (a.rego || "").localeCompare(b.rego || "", "en", { sensitivity: "base", numeric: true }),
      );
      const now = new Date();
      const stampNz = new Intl.DateTimeFormat(tenantLocale(), {
        weekday: "long", day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
        timeZone: tenantTz(),
      }).format(now);
      const dateOnly = new Intl.DateTimeFormat(tenantLocale(), {
        day: "2-digit", month: "2-digit", year: "numeric",
        timeZone: tenantTz(),
      }).format(now);
      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
      ));
      const fmtDate = (iso) => {
        if (!iso) return "—";
        try {
          return new Intl.DateTimeFormat(tenantLocale(), {
            day: "2-digit", month: "short", year: "numeric",
            timeZone: tenantTz(),
          }).format(new Date(iso));
        } catch { return "—"; }
      };
      const rowsHtml = sorted.map((r, i) => `
        <tr>
          <td class="num">${i + 1}</td>
          <td class="mono">${esc(r.rego || "—")}</td>
          <td>${esc(r.vehicle || "—")}</td>
          <td>${esc(r.color || "—")}</td>
          <td>${esc(r.impound_owner_name || r.customer_name || "—")}</td>
          <td>${esc(fmtDate(r.released_to_nts_at))}</td>
          <td class="tick"></td>
        </tr>`).join("");
      const html = `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<title>${esc(salvageShort())} Owned-in-Lieu Disposal Worklist — ${esc(dateOnly)}</title>
<style>
  @page { size: A4; margin: 10mm 10mm 8mm 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; }
  header { display: flex; align-items: baseline; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 4px; margin-bottom: 8px; }
  header h1 { margin: 0; font-size: 18px; letter-spacing: -0.01em; }
  header .stamp { font-size: 10px; color: #475569; }
  .summary { font-size: 10px; color: #475569; margin-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  thead th { background: #f1f5f9; text-align: left; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; font-size: 9px; color: #334155; padding: 4px 8px; border-bottom: 1px solid #cbd5e1; }
  tbody td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  td.num, th.num { width: 24px; color: #64748b; text-align: right; font-variant-numeric: tabular-nums; }
  td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700; letter-spacing: 0.01em; }
  td.tick, th.tick { width: 26px; text-align: center; }
  td.tick { border: 1px solid #94a3b8; border-left: none; border-right: 1px solid #e2e8f0; }
  td.tick::before { content: ""; display: inline-block; width: 12px; height: 12px; border: 1.5px solid #64748b; border-radius: 2px; vertical-align: middle; }
  footer { margin-top: 10px; padding-top: 4px; border-top: 1px solid #cbd5e1; font-size: 9.5px; color: #64748b; display: flex; justify-content: space-between; }
  .empty { text-align: center; padding: 40px 0; color: #94a3b8; font-style: italic; }
  @media print {
    tr { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    footer { break-inside: avoid; page-break-inside: avoid; }
    .no-print { display: none !important; }
  }
  .no-print { position: fixed; top: 12px; right: 12px; }
  .no-print button { padding: 8px 16px; font-size: 13px; border: 1px solid #0f172a; background: #0f172a; color: white; border-radius: 4px; cursor: pointer; font-weight: 600; }
  .no-print button.secondary { background: white; color: #0f172a; margin-right: 6px; }
</style>
</head><body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print</button>
  </div>
  <header>
    <h1>${esc(salvageFull())} — Owned-in-Lieu Disposal Worklist</h1>
    <div class="stamp">${esc(stampNz)}</div>
  </header>
  <div class="summary">Vehicles currently in inventory: <strong>${sorted.length}</strong> · Tick each vehicle as it's disposed and hand this sheet back for reconciliation.</div>
  ${sorted.length > 0 ? `
    <table>
      <thead>
        <tr>
          <th class="num">#</th>
          <th>REGO</th>
          <th>Vehicle</th>
          <th>Colour</th>
          <th>Owner</th>
          <th>Signed over</th>
          <th class="tick">✓</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>` : `<div class="empty">No vehicles currently in ${esc(salvageShort())} inventory.</div>`}
  <footer>
    <span>Disposed by: __________________________</span>
    <span>Signature / Date: __________________________</span>
  </footer>
  <script>setTimeout(() => window.print(), 300);</script>
</body></html>`;
      const w = window.open("", "_blank", "width=900,height=1100");
      if (!w) {
        toast.error("Pop-up blocked — please allow pop-ups for this site.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      toast.error("Failed to build disposal worklist");
    } finally {
      setPrintingWorklist(false);
    }
  }, [rows]);

  // Dispatchers may reverse their own slip for 48 hours; after that it is a
  // business decision and takes an admin. The backend enforces this too.
  const canUndoSignover = useCallback((job) => {
    if (!can(user, "sign_over_vehicles")) return false;
    if (isAdmin) return true;
    if (!hasCap(user, "admin", "dispatcher")) return false;
    const signed = job.released_to_nts_at
      ? new Date(job.released_to_nts_at).getTime() : 0;
    return signed > 0 && (Date.now() - signed) / 3600000 <= 48;
  }, [isAdmin, user]);

  const onUndoDispose = async (job) => {
    if (!isAdmin) return;
    const ok = window.confirm(`Undo disposal for ${job.vehicle || "this vehicle"}? It will go back into salvage inventory.`);
    if (!ok) return;
    try {
      const updated = await undoDisposeNtsVehicle(job.id);
      setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Disposal reverted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to undo");
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => {
        const haystack = [
          r.vehicle, r.rego, r.make, r.model, r.color, r.year_of_manufacture,
          r.customer_name, r.customer_phone, r.impound_owner_name,
          r.impound_owner_phone, r.address, r.drop_off_address,
          r.impound_notice_number, r.police_reference, r.claim_number,
          r.nts_sold_to, r.tow_authority_number, r.work_order_number, r.notes,
        ].join(" ").toLowerCase();
        return haystack.includes(q);
      })
    : rows;
  // Sort alphabetically by rego — matches the printed Disposal List order
  // and the Yard Stock Take sheet, so scanning between the two is
  // frictionless. Rows without a rego sink to the bottom.
  const visible = [...filtered].sort((a, b) => {
    const ra = (a.rego || "").trim();
    const rb = (b.rego || "").trim();
    if (!ra && !rb) return 0;
    if (!ra) return 1;
    if (!rb) return -1;
    return ra.localeCompare(rb, "en", { sensitivity: "base", numeric: true });
  });

  // Reconciliation tiles consume `visible` rows so the totals reflect the
  // active filter (e.g. "Disposed" view shows only recovered totals).
  return (
    <div className="p-6 space-y-4" data-testid="nts-impound-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight flex items-center gap-2">
            <Truck className="h-7 w-7 text-amber-600" />
            Owned in Lieu
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Vehicles signed over in lieu of payment — inventory and disposal tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canViewPl && (
            <Button
              onClick={() => setShowPlDialog(true)}
              title={`Generate month-end P&L for ${salvageShort()} owned-in-lieu vehicles`}
              className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="nts-pl-report-btn"
            >
              <FileText className="h-4 w-4.5" /> Month-end P&amp;L
            </Button>
          )}
          <Button
            variant="outline"
            onClick={onPrintDisposalWorklist}
            disabled={printingWorklist || loading}
            title="Print a walk-the-yard disposal worklist of every vehicle currently in inventory — for the worker to tick off as they dispose of each one"
            data-testid="nts-print-worklist-btn"
          >
            <Printer className={cn("h-4 w-4.5", printingWorklist && "animate-pulse")} />
            Print Disposal List
          </Button>
          <Button
            variant="outline"
            onClick={manualRefresh}
            disabled={refreshing || loading}
            title="Refresh now (auto-polls every 30s)"
            data-testid="nts-refresh-btn"
          >
            <RotateCw className={cn("h-4 w-4 mr-1.5", (refreshing || loading) && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filter chips + search */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 rounded-md p-1" data-testid="nts-filter-chips">
          {[
            { v: "false", label: "In Inventory" },
            { v: "true", label: "Disposed" },
            { v: "all", label: "All" },
            { v: "archived", label: "Archived" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilter(opt.v)}
              className={cn(
                "px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded transition-colors",
                filter === opt.v
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100",
              )}
              data-testid={`nts-filter-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rego, vehicle, colour, owner, phone, notice #, buyer, address…"
            className="w-full pl-9 pr-9 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="nts-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="text-xs text-slate-500 ml-auto whitespace-nowrap">
          {loading ? "Loading…" : `${visible.length} record${visible.length === 1 ? "" : "s"}`}
        </div>
      </div>

      {/* Reconciliation summary */}
      {!loading && <NtsReconciliation rows={visible} />}

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Vehicle</th>
              <th className="text-left px-3 py-2 font-bold">Original Driver</th>
              <th className="text-left px-3 py-2 font-bold">Signed Over</th>
              <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Debt Written Off</th>
              <th className="text-left px-3 py-2 font-bold">Status</th>
              <th className="text-left px-3 py-2 font-bold">Buyer / Notes</th>
              <th className="text-right px-3 py-2 font-bold whitespace-nowrap">Sale $</th>
              <th className="text-right px-3 py-2 font-bold whitespace-nowrap">Net</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="nts-rows">
            {loading && <tr><td colSpan={9} className="text-center text-slate-500 py-12">Loading…</td></tr>}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={9} className="text-center text-slate-400 py-12">
                {filter === "false" ? `Nothing in ${salvageShort()} inventory right now.`
                  : filter === "true" ? "No disposed vehicles yet."
                  : `No ${salvageShort()} impound records.`}
              </td></tr>
            )}
            {!loading && visible.map((j) => (
              <NtsRow
                key={j.id}
                job={j}
                isAdmin={isAdmin}
                onClick={() => setDetailJob(j)}
                onDispose={setDisposeJob}
                onUndo={onUndoDispose}
                canUndoSignover={canUndoSignover(j)}
                onUndoSignover={setUndoSignoverJob}
                onPaperwork={setPaperworkJob}
              />
            ))}
          </tbody>
        </table>
      </div>

      <ImpoundDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
      />

      <DisposeNtsDialog
        job={disposeJob}
        open={!!disposeJob}
        onOpenChange={(o) => !o && setDisposeJob(null)}
        onDisposed={(updated) => {
          setRows((rs) => rs.map((r) => (r.id === updated.id ? updated : r)));
          // If we're on the In-Inventory filter, the now-disposed row should fall out.
          if (filter === "false") {
            setRows((rs) => rs.filter((r) => r.id !== updated.id));
          }
        }}
      />

      <UndoSignoverDialog
        job={undoSignoverJob}
        onClose={() => setUndoSignoverJob(null)}
        onDone={() => refresh()}
      />

      <NtsPaperworkDialog
        job={paperworkJob}
        open={!!paperworkJob}
        onOpenChange={(o) => {
          if (!o) {
            // Refresh the row count badge after closing — it might have changed.
            refresh();
            setPaperworkJob(null);
          }
        }}
      />

      <NtsMonthlyPlDialog
        open={showPlDialog}
        onOpenChange={setShowPlDialog}
      />
    </div>
  );
}
